import $ from 'jquery';

import JqueryUtils from 'chairisher/util/jquery';

import { isMotdBannerVisible } from 'chairisher/context/site';
import { syncListAriaHidden } from 'chairisher/util/accessibility';
import { isPlainObject } from 'chairisher/util/type';

/**
 * Widget that presents choices as a menu that may have multiple levels of selection
 *
 * @param {Object} settings
 * @param {boolean=} settings.areOnlyLeafNodesSelectable @see this.areOnlyLeafNodesSelectable
 * @param {Array.<Choice>=} settings.choices @see this.choices
 * @param {boolean=} settings.isGlobalSearch @see this.isGlobalSearch
 * @param {boolean=} settings.isHidden True indicates the menu should be hidden by default
 * @param {boolean=} settings.isMultiSelect @see this.isMultiSelect
 * @param {string=} settings.menuChoiceClassName @see this.menuChoiceClassName
 * @param {Menu=} settings.parentMenu @see this.parentMenu
 * @param {string=} settings.menuSuggestionListClassName @see this.menuSuggestionListClassName
 * @param {string=} settings.selectedChoiceClassName @see this.selectedChoiceClassName
 * @param {Array.<Choice>=} settings.treeChoices @see this.treeChoices
 *
 * @constructor
 * @class Menu
 */
const Menu = function Menu(settings) {
    const options = $.extend(
        {
            areOnlyLeafNodesSelectable: this.areOnlyLeafNodesSelectable,
            choices: [],
            dataDisplayKey: this.dataDisplayKey,
            isGlobalSearch: this.isGlobalSearch,
            isHidden: false,
            isMultiSelect: this.isMultiSelect,
            menuChoiceClassName: this.menuChoiceClassName,
            menuChoiceContentTemplateSelector: this.menuChoiceContentTemplateSelector,
            menuChoiceHasThumbnail: true,
            menuElClasses: this.menuElClass,
            menuSuggestionListClassName: this.menuSuggestionListClassName,
            parentMenu: this.parentMenu,
            selectedChoiceClassName: this.selectedChoiceClassName,
            treeChoices: [],
        },
        settings,
    );

    this.choices = options.choices;
    this.treeChoices = options.treeChoices;

    this.choiceIndextoSubmenuMap = {};
    this.parentMenu = options.parentMenu;

    this.areOnlyLeafNodesSelectable = options.areOnlyLeafNodesSelectable;
    this.dataDisplayKey = options.dataDisplayKey;
    this.isGlobalSearch = options.isGlobalSearch;
    this.menuChoiceClassName = options.menuChoiceClassName;
    this.menuChoiceHasThumbnail = !!options.menuChoiceHasThumbnail;
    this.menuElClass = options.menuElClasses;
    this.menuSuggestionListClassName = options.menuSuggestionListClassName;
    this.menuChoiceContentTemplateSelector = options.menuChoiceContentTemplateSelector;
    this.selectedChoiceClassName = options.selectedChoiceClassName;

    this.$menuChoiceContentTemplate = $($(this.menuChoiceContentTemplateSelector).html());

    this.initializeMenu(options.isHidden);
    this.updateSuggestionChoices(this.choices);
    this.updateTreeChoices(this.treeChoices);
    this.bind();
};

/**
 * An object containing all tree choices at one level for easy lookup. Should be null everywhere except root menu
 *
 * @type {Object|null}
 */
Menu.prototype.allTreeChoices = null;

/**
 * Flag that indicates if all nodes are selectable or just leaf nodes.
 * Only applicable to menu choices that contain boolean inputs
 *
 * @type {boolean}
 */
Menu.prototype.areOnlyLeafNodesSelectable = false;

/**
 * A collection of Choice datastructures that are available as suggestions
 *
 * @type {Array.<Choice>}
 */
Menu.prototype.choices = null;

/**
 * Maps the index of a menu choice to a submenu that can be displayed by pressing the right arrow
 *
 * @type {Object.<string, Menu>}
 */
Menu.prototype.choiceIndextoSubmenuMap = null;

Menu.prototype.dataDisplayKey = 'display';

/**
 * Flag that indicates if menu is part of the global search
 *
 * @type {boolean}
 * @default
 */
Menu.prototype.isGlobalSearch = false;

/**
 * Flag that indicates if the choices are multi-select or not
 *
 * @type {boolean}
 */
Menu.prototype.isMultiSelect = true;

/**
 * An instance of the current menu's parent menu, if any
 *
 * @type {Menu|null}
 */
Menu.prototype.parentMenu = null;

/**
 * A class name used to decorate the currently selected choice
 *
 * @type {string}
 */
Menu.prototype.selectedChoiceClassName = 'selected';

/**
 * Used to traverse the DOM to choose a selected choice
 *
 * @type {string}
 */
Menu.prototype.selectedChoiceSelector = '.js-selected';

/**
 * Flag that indicates if menu choices should expect to display a thumbnail image or not
 *
 * @type {boolean}
 */
Menu.prototype.menuChoiceHasThumbnail = true;

/**
 * Class used to style the `.js-menu` element; can be customized when constructing a new Menu
 *
 * @type {string}
 */
Menu.prototype.menuElClass = 'menu';

/**
 * Used to traverse the DOM to select a menu
 *
 * @type {string}
 */
Menu.prototype.menuElSelector = '.js-menu';

/**
 * Class used to style a `.js-menu-choice` element; can be customized when constructing a new Menu
 *
 * @type {string}
 */
Menu.prototype.menuChoiceClassName = 'menu-choice';

/**
 * Selector of the element that contains the template to use when marshaling menu choices
 *
 * @type {string}
 */
Menu.prototype.menuChoiceContentTemplateSelector = '#js-template-menu-choice-content';

/**
 * Used to traverse the DOM to select a menu choice
 *
 * @type {string}
 */
Menu.prototype.menuChoiceSelector = '.js-menu-choice';

/**
 * Class used to style a `.js-menu-suggestion-list` element; can be customized when constructing a new Menu
 *
 * @type {string}
 */
Menu.prototype.menuSuggestionListClassName = 'menu-suggestion-list';

/**
 * Used to traverse the DOM to select a menu suggestion list
 *
 * @type {string}
 */
Menu.prototype.menuSuggestionListSelector = '.js-menu-suggestion-list';

/**
 * Class used to style a `.js-menu-tree-list` element; can be customized when constructing a new Menu
 *
 * @type {string}
 */
Menu.prototype.menuTreeListClassName = 'menu-tree-list';

/**
 * Used to traverse the DOM to select a menu tree list
 *
 * @type {string}
 */
Menu.prototype.menuTreeListSelector = '.js-menu-tree-list';

/**
 * A collection of Choice datastructures that are available as constant menu choices
 *
 * @type {Array.<Choice>}
 */
Menu.prototype.treeChoices = null;

/**
 * The jQuery element that serves as a template for marshaling `.js-menu-choice-content` element
 *
 * @type {jQuery}
 */
Menu.prototype.$menuChoiceContentTemplate = null;

/**
 * jQuery element representing the complete menu element
 *
 * @type {jQuery}
 */
Menu.prototype.$menuEl = null;

/**
 * jQuery element that contains the list of suggested choices that come from the server
 *
 * @type {jQuery}
 */
Menu.prototype.$menuSuggestionListEl = null;

/**
 * jQuery element that contains a list of choices organized in parent-child relationship
 *
 * @type {jQuery}
 */
Menu.prototype.$menuTreeListEl = null;

/**
 * Removes the selected class from all menu items. Helpful for clients that only want to allow unique choices
 * especially when the enter key is pressed.
 */
Menu.prototype.clearAllSelections = function clearAllSelections() {
    const $el = this.getRootMenu().getMenuEl().find(this.selectedChoiceSelector);
    $el.removeClass(this.selectedChoiceClassName);
    $el.removeClass(this.selectedChoiceSelector.substring(1));
};

/**
 * @param {jQuery=} $choiceEl The `.js-menu-choice` element to deselect
 */
Menu.prototype.deselectChoiceEl = function deselectChoiceEl($choiceEl) {
    let $target = $choiceEl;
    if (!$target || !$target.length) {
        const $rootMenuEl = this.getRootMenu().getMenuEl();
        $target = $rootMenuEl.find(this.selectedChoiceSelector);
    }
    this.toggleSelection($target, false);
};

/**
 * Returns a Choice object for a given key if this menu is the root menu.
 *
 * @param {string} key The key representing the Choice that should be returned
 * @returns {Choice|undefined} The Choice object for the given key
 * @see this.allTreeChoices
 */
Menu.prototype.getTreeChoiceForKey = function getTreeChoiceForKey(key) {
    return isPlainObject(this.allTreeChoices) ? this.allTreeChoices[key] : undefined;
};

/**
 * @returns {Array.<Choice>}
 */
Menu.prototype.getSuggestionChoices = function getSuggestionChoices() {
    return this.choices;
};

/**
 * @returns {Array.<Choice>}
 */
Menu.prototype.getTreeChoices = function getTreeChoices() {
    return this.treeChoices;
};

/**
 * @returns {Array.<jQuery>} a collection of this Menu's `.js-menu-choice` elements
 */
Menu.prototype.getChoiceEls = function getChoiceEls() {
    return this.$menuSuggestionListEl.children(this.menuChoiceSelector);
};

/**
 * @param {jQuery} $el The element whose index should be retrieved
 * @returns {number} The element's index
 */
Menu.prototype.getIndexForEl = function getIndexForEl($el) {
    return this.isGlobalSearch ? $el.index(this.menuChoiceSelector) : $el.index();
};

/**
 * @returns {Array.<jQuery>} a collection of this Menu's `.js-selected` elements
 */
Menu.prototype.getSelectedChoiceEl = function getSelectedChoiceEl() {
    return this.getChoiceEls().filter(this.selectedChoiceSelector);
};

/**
 * @param {number} index The zero-based index indicating which `.js-menu-choice` to select
 * @param {boolean=} shouldFocus True indicates selection should be made via :focus
 */
Menu.prototype.makeSelection = function makeSelection(index, shouldFocus) {
    const choiceIndex = this.getNextIndex(index);
    const $choice = this.getChoiceEls().eq(choiceIndex);

    if ($choice.length) {
        if (shouldFocus) {
            // once we trigger :focus on the new selection, :blur will automatically be triggered,
            // removing the selected state from the currently selected index ($selected)...
            $choice.trigger('focus');
        } else {
            this.selectChoiceEl($choice);
        }
    }
};

/**
 * @returns {jQuery} This Menu instance's `.js-menu` element
 */
Menu.prototype.getMenuEl = function getMenuEl() {
    return this.$menuEl;
};

/**
 * @param {number|undefined} desiredIndex The index that is attempting to be used
 * @returns {number} The final index to use
 */
Menu.prototype.getNextIndex = function getNextIndex(desiredIndex) {
    let index = desiredIndex;
    const choicesLength = this.choices.length;

    if (desiredIndex === undefined) {
        index = 0;
    } else if (desiredIndex > choicesLength - 1) {
        index = 0;
    } else if (desiredIndex < 0) {
        index = choicesLength - 1;
    }

    return index;
};

/**
 * @returns {Menu} The top most Menu in a collection of Menus
 */
Menu.prototype.getRootMenu = function getRootMenu() {
    let rootMenu = this;

    if (rootMenu.parentMenu) {
        rootMenu = this.parentMenu.getRootMenu();
    }

    return rootMenu;
};

/**
 * Hides the Menu
 */
Menu.prototype.hide = function hide() {
    this.toggle(false);
};

/**
 * @returns {boolean} True indicates this menu is visible
 */
Menu.prototype.isVisible = function isVisible() {
    return !this.$menuEl.hasClass('hidden');
};

/**
 * Removes the menu item choice at a given index from the ui and data
 *
 * @param {number} index The 0-based index indicating which item should be removed
 */
Menu.prototype.removeMenuEl = function removeMenuEl(index) {
    this.$menuSuggestionListEl.find(this.menuChoiceSelector).eq(index).remove();
    this.choices.splice(index);
};

/**
 * @param {jQuery} $choiceEl The `.js-menu-choice` element to select
 */
Menu.prototype.selectChoiceEl = function selectChoiceEl($choiceEl) {
    this.toggleSelection($choiceEl, true);
};

/**
 * Shows the Menu
 */
Menu.prototype.show = function show() {
    this.toggle(true);
};

/**
 * Toggles the Menu
 *
 * @param {boolean=} isVisible True indicate the Menu should be visible, False hidden, and undefined toggles
 */
Menu.prototype.toggle = function toggle(isVisible) {
    const shouldHide = isVisible !== undefined ? !isVisible : undefined;
    this.$menuEl.toggleClass('hidden', shouldHide);
};

/**
 * Updates the suggested choices and re-renders them
 *
 * @param {Array.<Choice>} choices The array choices to set
 */
Menu.prototype.updateSuggestionChoices = function updateSuggestionChoices(choices) {
    if (Array.isArray(choices)) {
        this.choices = choices;
        this.$menuSuggestionListEl.html(this.marshalMenuList(choices, this.dataDisplayKey).html());
        syncListAriaHidden(this.$menuSuggestionListEl[0]);
    }
};

/**
 * Updates the tree choices and re-renders them
 *
 * @param {Array.<Choice>} choices The array choices to set
 */
Menu.prototype.updateTreeChoices = function updateTreeChoices(choices) {
    if (Array.isArray(choices)) {
        this.treeChoices = choices;

        // update the flattened mapping of choice value to Choice object in the root menu...
        const choiceKeyToChoiceMap = {};
        for (let i = 0; i < this.treeChoices.length; i += 1) {
            const choice = this.treeChoices[i];
            choiceKeyToChoiceMap[choice.value] = choice;
        }
        const rootMenu = this.getRootMenu();
        const { allTreeChoices } = rootMenu;
        rootMenu.allTreeChoices = $.extend(allTreeChoices, choiceKeyToChoiceMap);

        // Note: you *must* use .children() and *cannot* use .html() because the
        // reference to the DOM fragments will be lost...
        const { $menuTreeListEl } = this;
        $menuTreeListEl.html(this.marshalMenuList(choices).children());
        syncListAriaHidden($menuTreeListEl[0]);

        const $menuChoices = $menuTreeListEl.children(this.menuChoiceSelector);

        // call .off() before .on() to ensure the choices are never double bound...
        $menuChoices.off('click').on(
            'click',
            $.proxy(function onTreeListChoiceClick(e) {
                e.stopPropagation();
                const $target = $(e.currentTarget);

                const submenu = this.choiceIndextoSubmenuMap[$target.index()];
                if (submenu) {
                    const isVisible = submenu.isVisible();

                    const $topMenu = $target.closest(this.menuElSelector);
                    $topMenu.find(this.menuElSelector).addClass('hidden');

                    $target.toggleClass('expanded', !isVisible);

                    if (!isVisible) {
                        submenu.show();
                    }

                    const $expandedMenuChoices = this.getRootMenu().getMenuEl().find('.expanded');
                    $.each($expandedMenuChoices, (i) => {
                        const $choice = $expandedMenuChoices.eq(i);
                        $choice.toggleClass('expanded', !$choice.children('.js-menu').hasClass('hidden'));
                    });
                }
            }, this),
        );
    }
};

/**
 * Binds all necessary events to make the Menu work
 */
Menu.prototype.bind = function bind() {
    const $menuEl = this.getMenuEl();

    const getValueFromTarget = $.proxy(function resolveSelectedChoiceFromTarget(target) {
        let $el = $(target);

        if (!$el.hasClass(this.menuChoiceClassName)) {
            $el = $el.closest(this.menuChoiceSelector);
        }

        const index = this.getIndexForEl($el);
        let selectedChoice;

        if ($el.parent(this.menuTreeListSelector).length) {
            selectedChoice = this.getTreeChoices()[index];
        } else {
            selectedChoice = this.getSuggestionChoices()[index];
        }

        selectedChoice.submenu = this.choiceIndextoSubmenuMap[index];

        return selectedChoice;
    }, this);

    $menuEl.on(
        'mousedown',
        '.js-boolean-input',
        $.proxy((e) => {
            e.preventDefault();
            e.stopPropagation();

            $menuEl.trigger('input:selected', getValueFromTarget(e.currentTarget));
        }, this),
    );

    $menuEl.on(
        'mousedown',
        this.menuChoiceSelector,
        $.proxy((e) => {
            // clicking on a menu choice results in a :focus event followed by a :blur event;
            e.preventDefault(); // preventing default cancels out the :blur event
            e.stopPropagation(); // when clicking in a submenu prevent the click from bubbling to the parent menu...

            $menuEl.trigger('menuchoice:selected', getValueFromTarget(e.currentTarget));
        }, this),
    );
};

/**
 * Builds the initial menu structure
 *
 * @param {boolean=} isHidden True indicates the menu should be hidden by default
 */
Menu.prototype.initializeMenu = function initializeMenu(isHidden) {
    const $menuEl = $('<div></div>', { class: this.menuElClass });
    JqueryUtils.decorateElementFromSelector($menuEl, this.menuElSelector);

    if (isMotdBannerVisible()) {
        $menuEl.addClass('banner-visible');
    }

    if (isHidden === true) {
        $menuEl.addClass('hidden');
    }

    const $menuSuggestionListEl = $('<ul></ul>', { 'class': this.menuSuggestionListClassName, 'aria-hidden': 'true' });
    JqueryUtils.decorateElementFromSelector($menuSuggestionListEl, this.menuSuggestionListSelector);

    const $menuTreeListEl = $('<ul></ul>', { 'class': this.menuTreeListClassName, 'aria-hidden': 'true' });
    JqueryUtils.decorateElementFromSelector($menuTreeListEl, this.menuTreeListSelector);

    $menuEl.append($menuSuggestionListEl, $menuTreeListEl);

    this.$menuEl = $menuEl;
    this.$menuSuggestionListEl = $menuSuggestionListEl;
    this.$menuTreeListEl = $menuTreeListEl;
};

/**
 * Creates a new menu list comprised of a given collection of Choice datastructures
 *
 * @param {Array.<Choice>} choices The array choices to render in the list
 * @param {string=} displayKey Optional key to use to choose display text; defaults to `display`
 * @returns {jQuery} The newly created menu list
 */
Menu.prototype.marshalMenuList = function marshalMenuList(choices, displayKey) {
    const $menuList = $('<ul></ul>');

    // only used for global searches
    const makeMenuHeading = (displayName) => $(`<li class='menu-heading' tabindex="-1">${displayName}</li>`);
    const menuChoicesGeneral = [];
    const menuChoicesMaker = [];
    const menuChoicesShop = [];

    for (let i = 0; i < choices.length; i += 1) {
        const choice = choices[i];

        const $menuChoice = $('<li></li>', {
            class: this.menuChoiceClassName,
            tabindex: 0,
        });
        JqueryUtils.decorateElementFromSelector($menuChoice, this.menuChoiceSelector);

        const $content = $(this.$menuChoiceContentTemplate[0].outerHTML);

        const $text = $content.find('.js-menu-choice-text');
        let choiceDisplay = choice[displayKey || 'display'];

        if (this.isGlobalSearch && choice.is_shop && choice.is_official_shop) {
            choiceDisplay += '<strong class="official-shop-badge">Official Shop</strong>';
        }

        $text.html(choiceDisplay);

        //
        // Add extra additional subtext to menu item
        //

        if (choice.additional_display) {
            const $additionalDisplay = $('<span />', {
                class: 'js-additional-menu-choice-text additional-menu-choice-text',
            });
            $additionalDisplay.text(choice.additional_display);
            $content.append($additionalDisplay);
            $content.addClass('menu-choice-column');
        }

        //
        // Add a URL to the choice if available for use in downstream simple dropdown menu clients
        //

        if (choice.url) {
            $content.attr('data-href', choice.url);
        }

        //
        // Draw the choice image if available
        //

        if (this.menuChoiceHasThumbnail) {
            if (choice.image) {
                const $img = $('<img />', {
                    alt: `Image of ${choice.display}`,
                    src: choice.image.src,
                    srcset: choice.image.srcset,
                    sizes: choice.image.sizes,
                });
                $content.find('.js-menu-choice-image').html($img);
            }
        } else {
            $content.find('.js-menu-choice-image').remove();
        }

        //
        // Set the appropriate values in the boolean input if available
        //

        const $input = $content.find('input');
        if ($input.length) {
            $input.attr({
                value: choice.value,
                type: this.isMultiSelect ? 'checkbox' : 'radio',
            });
        }

        $menuChoice.append($content);

        if (choice.children) {
            $text.append(
                $('<span></span>', {
                    class: 'cicon cicon-plussign',
                }),
            );

            $text.append(
                $('<span></span>', {
                    class: 'cicon cicon-minussign',
                }),
            );

            // todo: (CHAIR-5561) circle back and make this less brittle...
            if (this.areOnlyLeafNodesSelectable && $input.length) {
                $input.closest('.js-boolean-input').css('opacity', 0).removeClass('js-boolean-input');
                $input.remove();
            }

            const subMenu = new Menu({
                areOnlyLeafNodesSelectable: this.areOnlyLeafNodesSelectable,
                isHidden: true,
                menuChoiceContentTemplateSelector: this.menuChoiceContentTemplateSelector,
                menuChoiceHasThumbnail: this.menuChoiceHasThumbnail,
                parentMenu: this,
                treeChoices: choice.children,
            });
            $menuChoice.append(subMenu.getMenuEl());
            this.choiceIndextoSubmenuMap[i] = subMenu;
        }

        if (this.isGlobalSearch) {
            // sorts the choices into specific lists based on whether the choice is a maker, shop or neither
            if (choice.is_maker) {
                menuChoicesMaker.push($menuChoice);
            } else if (choice.is_shop) {
                menuChoicesShop.push($menuChoice);
            } else {
                menuChoicesGeneral.push($menuChoice);
            }
        } else {
            $menuList.append($menuChoice);
        }
    }

    if (this.isGlobalSearch) {
        // appends the choices lists in order - general (non-maker / non-shop) first, then maker and shop
        // this order is important and matches the response returned by the search_lookup_view
        $menuList.append(menuChoicesGeneral);
        if (menuChoicesMaker.length > 0) {
            menuChoicesMaker.unshift(makeMenuHeading('Brands & Creators'));
            $menuList.append(menuChoicesMaker);
        }
        if (menuChoicesShop.length > 0) {
            menuChoicesShop.unshift(makeMenuHeading('Shops'));
            $menuList.append(menuChoicesShop);
        }
    }

    return $menuList;
};

/**
 * Toggles classes for an element that make it selected or not
 *
 * @param {jQuery} $el The element whose classes should be toggled
 * @param {boolean=} isSelected Optional flag that indicates if the classes should be removed or not
 */
Menu.prototype.toggleSelection = function toggleSelection($el, isSelected) {
    $el.toggleClass([this.selectedChoiceClassName, this.selectedChoiceSelector.replace('.', '')].join(' '), isSelected);
};

export default Menu;
